import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import {
    getAllPartnerships,
    createPartnership,
    updatePartnership,
    deletePartnership,
    checkProductCategoryOverlaps,
} from '../../services/partnershipsApi';
import { listProductProviders } from '../../services/providersApi';
import { getCategories } from '../../services/categoriesApi';
import { uploadFile, getPhoto } from '../../storageService';
import Spinner from '../Spinner/Spinner';
import './PartnershipManagement.css';

const emptyForm = () => ({
    id: null,
    productProviderId: '',
    sponsoredServiceCategories: [],
    description: '',
    active: true,
    startDate: '',
    endDate: '',
    bannerPhoto: '',
    popupConfig: { enabled: false, title: '', image: '', ctaUrl: '' },
});

const PartnershipManagement = () => {
    const [partnerships, setPartnerships] = useState([]);
    const [loading, setLoading] = useState(true);
    const [productProviders, setProductProviders] = useState([]);
    const [serviceCategories, setServiceCategories] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [overlaps, setOverlaps] = useState([]);
    const [bannerFile, setBannerFile] = useState(null);
    const [popupImageFile, setPopupImageFile] = useState(null);

    // ============= FETCH INICIAL =============

    const fetchPartnerships = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getAllPartnerships();
            setPartnerships(data || []);
        } catch (error) {
            console.error('Error fetching partnerships:', error);
            toast.error('No se pudieron cargar los partnerships');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPartnerships();
    }, [fetchPartnerships]);

    useEffect(() => {
        // Cargar lista de providers de productos y categorías de servicios para los selects del form.
        (async () => {
            try {
                const [providers, services] = await Promise.all([
                    listProductProviders(),
                    getCategories('servicios'),
                ]);
                setProductProviders(Array.isArray(providers) ? providers : []);
                setServiceCategories(Array.isArray(services) ? services : []);
            } catch (error) {
                console.error('Error loading providers/categories:', error);
            }
        })();
    }, []);

    // ============= ABRIR / CERRAR MODAL =============

    const openCreate = () => {
        setForm(emptyForm());
        setOverlaps([]);
        setBannerFile(null);
        setPopupImageFile(null);
        setShowModal(true);
    };

    const openEdit = (partnership) => {
        setForm({
            id: partnership.id,
            productProviderId: partnership.productProviderId || '',
            sponsoredServiceCategories: partnership.sponsoredServiceCategories || [],
            description: partnership.description || '',
            active: !!partnership.active,
            startDate: partnership.startDate ? toInputDate(partnership.startDate) : '',
            endDate: partnership.endDate ? toInputDate(partnership.endDate) : '',
            bannerPhoto: partnership.bannerPhoto || '',
            popupConfig: partnership.popupConfig || { enabled: false, title: '', image: '', ctaUrl: '' },
        });
        setOverlaps([]);
        setBannerFile(null);
        setPopupImageFile(null);
        setShowModal(true);
    };

    const closeModal = () => {
        if (saving) return;
        setShowModal(false);
    };

    // ============= WARNING BLANDO: PISADA DE CATEGORÍAS DE PRODUCTOS =============

    useEffect(() => {
        if (!form.productProviderId) {
            setOverlaps([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const result = await checkProductCategoryOverlaps(form.productProviderId, form.id || null);
                if (!cancelled) setOverlaps(result || []);
            } catch (error) {
                if (!cancelled) {
                    console.error('Error checking overlaps:', error);
                    setOverlaps([]);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [form.productProviderId, form.id]);

    // ============= SAVE =============

    const handleSave = async () => {
        if (!form.productProviderId) {
            toast.error('Tenés que elegir un provider de productos');
            return;
        }
        if (!form.description?.trim()) {
            toast.error('La descripción es obligatoria — explicá los beneficios para otros providers');
            return;
        }

        setSaving(true);
        try {
            // 1) Subir banner si hay archivo nuevo
            let bannerPath = form.bannerPhoto;
            const partnershipId = form.id || cryptoRandomId();
            if (bannerFile) {
                const ext = (bannerFile.name?.split('.').pop() || 'jpg').toLowerCase();
                bannerPath = `partnerships/${partnershipId}/banner-${Date.now()}.${ext}`;
                await uploadFile(bannerFile, bannerPath);
            }

            // 2) Subir imagen de pop-up si hay archivo nuevo
            let popupImagePath = form.popupConfig?.image || '';
            if (popupImageFile) {
                const ext = (popupImageFile.name?.split('.').pop() || 'jpg').toLowerCase();
                popupImagePath = `partnerships/${partnershipId}/popup-${Date.now()}.${ext}`;
                await uploadFile(popupImageFile, popupImagePath);
            }

            // 3) Construir payload
            const payload = {
                id: form.id || partnershipId,
                productProviderId: form.productProviderId,
                sponsoredServiceCategories: form.sponsoredServiceCategories,
                description: form.description.trim(),
                active: form.active,
                startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
                endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
                bannerPhoto: bannerPath || '',
                popupConfig: {
                    enabled: !!form.popupConfig?.enabled,
                    title: form.popupConfig?.title || '',
                    image: popupImagePath,
                    ctaUrl: form.popupConfig?.ctaUrl || '',
                },
            };

            // 4) Crear o actualizar
            if (form.id) {
                await updatePartnership(form.id, payload);
                toast.success('Partnership actualizado');
            } else {
                await createPartnership(payload);
                toast.success('Partnership creado');
            }

            setShowModal(false);
            fetchPartnerships();
        } catch (error) {
            const status = error?.response?.status;
            const message = error?.response?.data || error?.message || 'Error guardando partnership';
            if (status === 409) {
                // Validación dura: pisada de categorías de servicios
                toast.error(typeof message === 'string'
                    ? message
                    : 'Ya hay otro partnership activo sponsoreando esa categoría de servicios');
            } else {
                toast.error(typeof message === 'string' ? message : 'Error guardando partnership');
            }
        } finally {
            setSaving(false);
        }
    };

    // ============= DELETE =============

    const handleDelete = async (partnership) => {
        if (!window.confirm(`¿Eliminar el partnership de ${partnership.partnerName || partnership.productProviderId}?`)) {
            return;
        }
        try {
            await deletePartnership(partnership.id);
            toast.success('Partnership eliminado');
            fetchPartnerships();
        } catch (error) {
            console.error('Error deleting partnership:', error);
            toast.error('No se pudo eliminar el partnership');
        }
    };

    // ============= TOGGLES DE CATEGORÍAS =============

    const toggleSponsoredCategory = (categoryId) => {
        setForm((prev) => {
            const has = prev.sponsoredServiceCategories.includes(categoryId);
            return {
                ...prev,
                sponsoredServiceCategories: has
                    ? prev.sponsoredServiceCategories.filter((id) => id !== categoryId)
                    : [...prev.sponsoredServiceCategories, categoryId],
            };
        });
    };

    // ============= RENDER =============

    if (loading) {
        return <div style={{ padding: 24 }}><Spinner /></div>;
    }

    return (
        <div className="partnership-mgmt">
            <div className="partnership-mgmt__header">
                <h2>Gestión de Partnerships</h2>
                <button className="partnership-mgmt__btn-primary" onClick={openCreate}>
                    <FaPlus /> Nuevo Partnership
                </button>
            </div>

            {partnerships.length === 0 ? (
                <p className="partnership-mgmt__empty">
                    Todavía no hay partnerships. Creá el primero con el botón de arriba.
                </p>
            ) : (
                <table className="partnership-mgmt__table">
                    <thead>
                        <tr>
                            <th>Partner</th>
                            <th>Categorías sponsoreadas</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {partnerships.map((p) => (
                            <tr key={p.id}>
                                <td>{p.partnerName || p.productProviderId}</td>
                                <td>{(p.sponsoredServiceCategories || []).join(', ') || '—'}</td>
                                <td>
                                    {p.active
                                        ? <span className="partnership-mgmt__badge-active"><FaCheck /> Activo</span>
                                        : <span className="partnership-mgmt__badge-inactive"><FaTimes /> Inactivo</span>}
                                </td>
                                <td>
                                    <button className="partnership-mgmt__btn-icon" onClick={() => openEdit(p)} title="Editar">
                                        <FaEdit />
                                    </button>
                                    <button
                                        className="partnership-mgmt__btn-icon partnership-mgmt__btn-icon--danger"
                                        onClick={() => handleDelete(p)}
                                        title="Eliminar"
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {showModal && (
                <div className="partnership-mgmt__modal-backdrop" onClick={closeModal}>
                    <div className="partnership-mgmt__modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{form.id ? 'Editar Partnership' : 'Nuevo Partnership'}</h3>

                        <label className="partnership-mgmt__field">
                            <span>Provider de productos</span>
                            <select
                                value={form.productProviderId}
                                onChange={(e) => setForm({ ...form, productProviderId: e.target.value })}
                                disabled={!!form.id}
                            >
                                <option value="">— Elegir —</option>
                                {productProviders.map((username) => (
                                    <option key={username} value={username}>{username}</option>
                                ))}
                            </select>
                        </label>

                        {overlaps.length > 0 && (
                            <div className="partnership-mgmt__warning">
                                ⚠️ Este provider comparte categorías de productos con otro(s) partnership(s) activo(s):
                                <ul style={{ margin: '4px 0 0 18px' }}>
                                    {overlaps.map((o) => (
                                        <li key={o.id}>{o.partnerName || o.productProviderId}</li>
                                    ))}
                                </ul>
                                <small>Podés avanzar igual, es solo un aviso.</small>
                            </div>
                        )}

                        <label className="partnership-mgmt__field">
                            <span>Categorías de servicios sponsoreadas</span>
                            <div className="partnership-mgmt__checkbox-grid">
                                {serviceCategories.map((cat) => (
                                    <label key={cat.id} className="partnership-mgmt__checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={form.sponsoredServiceCategories.includes(cat.id)}
                                            onChange={() => toggleSponsoredCategory(cat.id)}
                                        />
                                        {cat.name}
                                    </label>
                                ))}
                            </div>
                        </label>

                        <label className="partnership-mgmt__field">
                            <span>Descripción (beneficios para otros providers)</span>
                            <textarea
                                rows={3}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                placeholder="Ej: Descuentos en pinturas premium para pintores asociados..."
                            />
                        </label>

                        <div style={{ display: 'flex', gap: 16 }}>
                            <label className="partnership-mgmt__field" style={{ flex: 1 }}>
                                <span>Inicio</span>
                                <input
                                    type="date"
                                    value={form.startDate}
                                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                                />
                            </label>
                            <label className="partnership-mgmt__field" style={{ flex: 1 }}>
                                <span>Fin (opcional)</span>
                                <input
                                    type="date"
                                    value={form.endDate}
                                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                                />
                            </label>
                        </div>

                        <label className="partnership-mgmt__field">
                            <span>Banner (foto chica bajo categoría sponsoreada)</span>
                            <input type="file" accept="image/*" onChange={(e) => setBannerFile(e.target.files?.[0] || null)} />
                            {form.bannerPhoto && !bannerFile && (
                                <small>Actual: {form.bannerPhoto}</small>
                            )}
                        </label>

                        <fieldset className="partnership-mgmt__fieldset">
                            <legend>Pop-up en Home</legend>
                            <label className="partnership-mgmt__checkbox-row">
                                <input
                                    type="checkbox"
                                    checked={!!form.popupConfig?.enabled}
                                    onChange={(e) => setForm({
                                        ...form,
                                        popupConfig: { ...form.popupConfig, enabled: e.target.checked },
                                    })}
                                />
                                Mostrar pop-up
                            </label>
                            <label className="partnership-mgmt__field">
                                <span>Título</span>
                                <input
                                    type="text"
                                    value={form.popupConfig?.title || ''}
                                    onChange={(e) => setForm({
                                        ...form,
                                        popupConfig: { ...form.popupConfig, title: e.target.value },
                                    })}
                                />
                            </label>
                            <label className="partnership-mgmt__field">
                                <span>Imagen del pop-up</span>
                                <input type="file" accept="image/*" onChange={(e) => setPopupImageFile(e.target.files?.[0] || null)} />
                                {form.popupConfig?.image && !popupImageFile && (
                                    <small>Actual: {form.popupConfig.image}</small>
                                )}
                            </label>
                            <label className="partnership-mgmt__field">
                                <span>URL de CTA</span>
                                <input
                                    type="text"
                                    value={form.popupConfig?.ctaUrl || ''}
                                    onChange={(e) => setForm({
                                        ...form,
                                        popupConfig: { ...form.popupConfig, ctaUrl: e.target.value },
                                    })}
                                />
                            </label>
                        </fieldset>

                        <label className="partnership-mgmt__checkbox-row">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                            />
                            Activo
                        </label>

                        <div className="partnership-mgmt__actions">
                            <button className="partnership-mgmt__btn-secondary" onClick={closeModal} disabled={saving}>
                                Cancelar
                            </button>
                            <button className="partnership-mgmt__btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ============= UTILS =============

function toInputDate(value) {
    try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().slice(0, 10);
    } catch {
        return '';
    }
}

function cryptoRandomId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `pt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default PartnershipManagement;
